import React from 'react';
import type { ModuleDefinition } from './moduleRegistry';
import type { ReportData } from '../data/types';
import { ScreenSection, ScreenKpiGrid, ScreenDataTable, ScreenDivider } from '../primitives/ScreenPrimitives';
import { fmtAmtCr, fmtDate } from './formatUtils';

const ScreenFunding: React.FC<{ data: ReportData; index: string }> = ({ data, index }) => {
    const f = data.funding;
    if (!f) return null;
    const rows = f.fundingRows.map((r) => ({
        date: fmtDate(r.date),
        amount: r.amount,
        instrument: r.instrument,
        type: r.type,
    }));
    const debt = f.debentureRows.map((r) => ({
        date: fmtDate(r.date),
        amount: r.amount,
        instrument: r.instrument,
        type: r.type,
    }));
    return (
        <ScreenSection kicker="Capital flows" title="Funding & debenture history" index={index}>
            <ScreenKpiGrid
                items={[
                    { label: 'Total funding raised', value: fmtAmtCr(f.totalFundingCr) },
                    { label: 'Total debentures', value: fmtAmtCr(f.totalDebentureCr) },
                    { label: 'Rounds recorded', value: String(f.fundingRows.length) },
                    { label: 'Last updated', value: fmtDate(f.lastUpdatedAt) },
                ]}
            />
            {rows.length ? (
                <div style={{ marginTop: 10 }}>
                    <div style={{ fontFamily: 'Gilroy-Bold', marginBottom: 4 }}>Equity funding</div>
                    <ScreenDataTable
                        columns={[
                            { key: 'date', header: 'Date', width: '18%' },
                            { key: 'amount', header: 'Amount', width: '22%' },
                            { key: 'instrument', header: 'Instrument' },
                            { key: 'type', header: 'Type', width: '22%' },
                        ]}
                        rows={rows}
                    />
                </div>
            ) : null}
            {debt.length ? (
                <>
                    <ScreenDivider />
                    <div style={{ fontFamily: 'Gilroy-Bold', marginBottom: 4 }}>Debenture issuances</div>
                    <ScreenDataTable
                        columns={[
                            { key: 'date', header: 'Date', width: '18%' },
                            { key: 'amount', header: 'Amount', width: '22%' },
                            { key: 'instrument', header: 'Instrument' },
                            { key: 'type', header: 'Type', width: '22%' },
                        ]}
                        rows={debt}
                    />
                </>
            ) : null}
        </ScreenSection>
    );
};

export const fundingModule: ModuleDefinition = {
    meta: {
        id: 'funding',
        title: 'Funding history',
        description: 'Equity + debenture issuances.',
        dataSource: 'MCA Funding History',
    },
    hasData: (d) => !!d.funding && (d.funding.fundingRows.length > 0 || d.funding.debentureRows.length > 0),
    Screen: ScreenFunding,
};
